/** Instance-owned effects extracted from the separately maintained Steam Punk game.
 * The host supplies clock, randomness, DOM and lifecycle; no globals or score API are shared. */
export function installSteamEffects(runtime) {
  runtime.drawBall = function (ball) {
    if (!ball || !Number.isFinite(ball.x) || !Number.isFinite(ball.y)) return;
    const crowded = runtime.game.drawQuality === 'low' || runtime.game.balls.length > 80;
    const busy = runtime.game.drawQuality === 'medium' || runtime.game.balls.length > 45;
    const trailStep = crowded ? 3 : busy ? 2 : 1;
    runtime.ctx.save();
    for (let i = ball.trail.length - 1; i >= 0; i -= trailStep) {
      const t = ball.trail[i];
      if (!t || !Number.isFinite(t.x) || !Number.isFinite(t.y)) continue;
      runtime.ctx.globalAlpha = (ball.trail.length - i) / ball.trail.length / 2.1;
      runtime.ctx.beginPath();
      runtime.ctx.arc(t.x, t.y, Math.max(1.2, ball.r * (1 - i / 12)), 0, Math.PI * 2);
      runtime.ctx.fillStyle = ball.neutron
        ? i % 2
          ? '#fff8f0'
          : '#ff4fd8'
        : i % 2
          ? '#b85f1c'
          : '#ffd166';
      runtime.ctx.fill();
    }
    runtime.ctx.globalAlpha = 1;
    const sprite = runtime.getBallSprite(
      ball.neutron ? 'neutron' : ball.temporary ? 'temporary' : 'normal',
      crowded ? 'crowded' : busy ? 'busy' : 'normal',
    );
    runtime.ctx.drawImage(
      sprite,
      Math.round(ball.x - sprite.width / 2),
      Math.round(ball.y - sprite.height / 2),
    );
    if (ball.neutron) {
      runtime.ctx.strokeStyle = 'rgba(255,248,240,0.92)';
      runtime.ctx.shadowColor = '#fff8f0';
      runtime.ctx.shadowBlur = 16;
      runtime.ctx.lineWidth = 2.4;
      runtime.ctx.beginPath();
      runtime.ctx.arc(ball.x, ball.y, ball.r + 8, 0, Math.PI * 2);
      runtime.ctx.stroke();
    }
    if (ball.temporary) {
      runtime.ctx.strokeStyle = '#ffd166';
      runtime.ctx.lineWidth = 2;
      runtime.ctx.beginPath();
      runtime.ctx.arc(
        ball.x,
        ball.y,
        ball.r + 7,
        -Math.PI / 2,
        -Math.PI / 2 +
          Math.PI * 2 * runtime.clamp(ball.hitsLeft / Math.max(1, runtime.game.level * 10), 0, 1),
      );
      runtime.ctx.stroke();
    }
    runtime.ctx.restore();
  };
  runtime.getBallSprite = function (kind, quality) {
    const key = `${kind}:${quality}`;
    if (runtime.ballSpriteCache.has(key)) return runtime.ballSpriteCache.get(key);
    const radius = quality === 'crowded' ? 9 : quality === 'busy' ? 10 : 13;
    const sprite = document.createElement('canvas');
    sprite.width = radius * 2 + 8;
    sprite.height = radius * 2 + 8;
    const target = sprite.getContext('2d');
    const cx = sprite.width / 2;
    const cy = sprite.height / 2;
    const glowColor = kind === 'neutron' ? '#fff8f0' : kind === 'temporary' ? '#ffcf6a' : '#d97a24';
    const emberColor =
      kind === 'neutron' ? '#ff4fd8' : kind === 'temporary' ? '#fff0a8' : '#ff9f3d';
    if (quality !== 'crowded') {
      target.shadowColor = glowColor;
      target.shadowBlur = quality === 'busy' ? 12 : 18;
      target.beginPath();
      target.arc(cx, cy, radius - 1, 0, Math.PI * 2);
      target.fillStyle = 'rgba(255,159,61,0.22)';
      target.fill();
    }
    target.shadowBlur = 0;
    const shell = target.createRadialGradient(
      cx - radius * 0.25,
      cy - radius * 0.34,
      1,
      cx,
      cy,
      radius,
    );
    shell.addColorStop(0, '#fffefa');
    shell.addColorStop(0.24, kind === 'neutron' ? '#fff8f0' : '#ffd166');
    shell.addColorStop(0.5, emberColor);
    shell.addColorStop(0.78, kind === 'neutron' ? '#9b1b82' : '#8f3a0a');
    shell.addColorStop(1, kind === 'neutron' ? '#19042f' : '#2b1003');
    target.beginPath();
    target.arc(cx, cy, radius * 0.62, 0, Math.PI * 2);
    target.fillStyle = shell;
    target.fill();
    target.strokeStyle = 'rgba(255,238,180,0.72)';
    target.lineWidth = 1.2;
    target.stroke();
    target.beginPath();
    target.arc(cx - radius * 0.24, cy - radius * 0.28, radius * 0.22, 0, Math.PI * 2);
    target.fillStyle = '#fff8dc';
    target.fill();
    runtime.ballSpriteCache.set(key, sprite);
    return sprite;
  };
  runtime.drawParticle = function (p) {
    const alpha = runtime.clamp(p.life / p.maxLife, 0, 1);
    runtime.ctx.globalAlpha = alpha;
    if (p.type === 'coin') {
      runtime.ctx.save();
      runtime.ctx.translate(p.x, p.y);
      runtime.ctx.rotate(p.spin || 0);
      runtime.ctx.scale(Math.max(0.35, Math.abs(Math.cos(p.spin || 0))), 1);
      runtime.ctx.fillStyle = '#ffd166';
      runtime.ctx.shadowColor = '#ffd166';
      runtime.ctx.shadowBlur = 12;
      runtime.ctx.beginPath();
      runtime.ctx.arc(0, 0, p.r, 0, Math.PI * 2);
      runtime.ctx.fill();
      runtime.ctx.strokeStyle = '#fff4c2';
      runtime.ctx.lineWidth = 1.2;
      runtime.ctx.stroke();
      runtime.ctx.fillStyle = '#4a1a05';
      runtime.ctx.font = `900 ${Math.max(8, p.r * 1.3)}px Sora, Inter, sans-serif`;
      runtime.ctx.textAlign = 'center';
      runtime.ctx.textBaseline = 'middle';
      runtime.ctx.fillText('$', 0, 0.5);
      runtime.ctx.restore();
      runtime.ctx.globalAlpha = 1;
      return;
    }
    if (p.type === 'moneyBag') {
      runtime.ctx.save();
      runtime.ctx.translate(p.x, p.y);
      runtime.ctx.rotate(Math.sin(p.spin || 0) * 0.18);
      runtime.ctx.shadowColor = '#74ff8d';
      runtime.ctx.shadowBlur = 14;
      runtime.ctx.fillStyle = '#74ff8d';
      runtime.roundRectCtx(runtime.ctx, -p.r * 0.72, -p.r * 0.2, p.r * 1.44, p.r * 1.25, 5);
      runtime.ctx.fill();
      runtime.ctx.fillStyle = '#1f521f';
      runtime.roundRectCtx(runtime.ctx, -p.r * 0.42, -p.r * 0.88, p.r * 0.84, p.r * 0.58, 4);
      runtime.ctx.fill();
      runtime.ctx.fillStyle = '#07350e';
      runtime.ctx.font = `900 ${Math.max(10, p.r * 1.12)}px Sora, Inter, sans-serif`;
      runtime.ctx.textAlign = 'center';
      runtime.ctx.textBaseline = 'middle';
      runtime.ctx.fillText('$', 0, p.r * 0.34);
      runtime.ctx.restore();
      runtime.ctx.globalAlpha = 1;
      return;
    }
    runtime.ctx.fillStyle = p.color;
    runtime.ctx.beginPath();
    runtime.ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
    runtime.ctx.fill();
    runtime.ctx.globalAlpha = 1;
  };
  runtime.drawFloater = function (floater) {
    runtime.ctx.save();
    runtime.ctx.globalAlpha = runtime.clamp(floater.life / floater.maxLife, 0, 1);
    runtime.ctx.shadowColor = floater.color;
    runtime.ctx.shadowBlur = floater.shadowBlur || 16;
    runtime.ctx.strokeStyle = floater.stroke || 'rgba(37,12,3,0.75)';
    const fontSize = Math.round(16 * (floater.scale || 1));
    runtime.ctx.lineWidth = Math.max(4, Math.round(4 * (floater.scale || 1)));
    runtime.ctx.font = `900 ${fontSize}px Sora, Inter, sans-serif`;
    runtime.ctx.textAlign = 'center';
    runtime.ctx.textBaseline = 'middle';
    runtime.ctx.strokeText(floater.text, floater.x, floater.y);
    runtime.ctx.fillStyle = floater.color;
    runtime.ctx.font = `900 ${fontSize}px Sora, Inter, sans-serif`;
    runtime.ctx.textAlign = 'center';
    runtime.ctx.textBaseline = 'middle';
    runtime.ctx.fillText(floater.text, floater.x, floater.y);
    runtime.ctx.restore();
  };
  runtime.drawBurst = function (burst) {
    const alpha = runtime.clamp(burst.life / burst.maxLife, 0, 1);
    runtime.ctx.save();
    runtime.ctx.globalCompositeOperation = 'lighter';
    runtime.ctx.globalAlpha = alpha;
    runtime.ctx.shadowColor = burst.color;
    runtime.ctx.shadowBlur = 18;
    runtime.ctx.strokeStyle = burst.color;
    runtime.ctx.lineWidth = burst.width * alpha;
    runtime.ctx.beginPath();
    runtime.ctx.arc(burst.x, burst.y, burst.radius, 0, Math.PI * 2);
    runtime.ctx.stroke();
    runtime.ctx.globalAlpha = alpha * 0.25;
    runtime.ctx.lineWidth = 1;
    runtime.ctx.beginPath();
    runtime.ctx.arc(burst.x, burst.y, burst.radius * 0.58, 0, Math.PI * 2);
    runtime.ctx.stroke();
    runtime.ctx.restore();
  };
  runtime.drawScreenFlash = function () {
    if (runtime.game.screenFlash <= 0) return;
    runtime.ctx.save();
    runtime.ctx.globalAlpha = runtime.game.screenFlash * 0.2;
    runtime.ctx.fillStyle = '#fff4c2';
    runtime.ctx.fillRect(0, 0, runtime.W, runtime.H);
    runtime.ctx.restore();
  };
  runtime.drawChaosShimmer = function () {
    if (runtime.game.chaosTurns <= 0 || runtime.game.chaosStacks <= 0) return;
    const t = runtime.now() * 0.006;
    const dotCount =
      runtime.game.energyMode === 'pressureGuard'
        ? 18
        : runtime.game.drawQuality === 'low'
          ? 28
          : runtime.game.drawQuality === 'medium'
            ? 44
            : 72;
    runtime.ctx.save();
    runtime.ctx.globalCompositeOperation = 'lighter';
    runtime.ctx.shadowColor = '#ffd166';
    runtime.ctx.shadowBlur = 22;
    runtime.ctx.lineWidth = 5 + Math.sin(t * 2) * 1.2;
    runtime.ctx.strokeStyle = 'rgba(255,209,102,0.8)';
    runtime.ctx.strokeRect(7, 7, runtime.W - 14, runtime.H - 14);
    runtime.ctx.shadowColor = '#ff8a3d';
    runtime.ctx.shadowBlur = 28;
    runtime.ctx.lineWidth = 3;
    runtime.ctx.strokeStyle = 'rgba(255,138,61,0.52)';
    runtime.ctx.strokeRect(18, 18, runtime.W - 36, runtime.H - 36);
    for (let i = 0; i < dotCount; i += 1) {
      const edge = i % 4;
      const n = (i / dotCount + t * 0.1) % 1;
      const jitter = Math.sin(t * 4 + i * 2.17) * 5;
      const x =
        edge === 0
          ? n * runtime.W
          : edge === 1
            ? runtime.W - 10 + jitter
            : edge === 2
              ? (1 - n) * runtime.W
              : 10 + jitter;
      const y =
        edge === 0
          ? 10 + jitter
          : edge === 1
            ? n * runtime.H
            : edge === 2
              ? runtime.H - 10 + jitter
              : (1 - n) * runtime.H;
      const r = 1.4 + Math.abs(Math.sin(t * 7 + i)) * 2.4;
      runtime.ctx.globalAlpha = 0.42 + Math.abs(Math.sin(t * 3 + i)) * 0.34;
      runtime.ctx.fillStyle = i % 5 === 0 ? '#ff8a3d' : i % 3 === 0 ? '#ffd166' : '#fff8dc';
      runtime.ctx.beginPath();
      runtime.ctx.arc(x, y, r, 0, Math.PI * 2);
      runtime.ctx.fill();
    }
    runtime.ctx.globalAlpha = 0.95;
    runtime.ctx.shadowColor = '#ffd166';
    runtime.ctx.shadowBlur = 16;
    runtime.ctx.fillStyle = '#fff4c2';
    runtime.ctx.font = '900 28px Sora, Inter, sans-serif';
    runtime.ctx.textAlign = 'right';
    runtime.ctx.textBaseline = 'top';
    runtime.ctx.fillText(`CHAOS ${runtime.chaosMultiplierLabel()}`, runtime.W - 18, 18);
    runtime.ctx.restore();
  };
  runtime.energyEffectScale = function () {
    const governorScale = runtime.currentEnergySettings().effectScale;
    if (runtime.game.energyMode === 'idle') return governorScale;
    if (!runtime.isMobilePressureMode()) return 1;
    if (
      runtime.game.launchFlash > 0 ||
      runtime.game.neutronBombsInDeployment > 0 ||
      runtime.game.screenFlash > 1
    )
      return Math.min(0.82, governorScale);
    return Math.min(runtime.game.boardShiftCue > 0 ? 0.42 : 0.62, governorScale);
  };
  runtime.particleCapacity = function () {
    return Math.max(24, runtime.currentEnergySettings().maxParticles);
  };
  runtime.spark = function (x, y, color, count) {
    const qualityScale =
      runtime.game.drawQuality === 'low' ? 0.38 : runtime.game.drawQuality === 'medium' ? 0.68 : 1;
    const total = Math.min(
      Math.ceil(count * qualityScale * runtime.EFFECT_QUALITY * runtime.energyEffectScale()),
      runtime.particleCapacity() - runtime.game.particles.length,
    );
    if (total <= 0) return;
    for (let i = 0; i < total; i += 1) {
      const angle = runtime.visualRand(0, Math.PI * 2);
      const speed = runtime.visualRand(42, 230);
      const life = runtime.visualRand(0.18, 0.52);
      runtime.game.particles.push({
        x,
        y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        r: runtime.visualRand(1.5, 4.5),
        color,
        life,
        maxLife: life,
      });
    }
  };
  runtime.spawnMoneyRain = function (reward) {
    const qualityScale =
      runtime.game.drawQuality === 'low' ? 0.52 : runtime.game.drawQuality === 'medium' ? 0.76 : 1;
    const requested = Math.ceil(
      (runtime.MOBILE_QUALITY ? 42 : 72) *
        qualityScale *
        runtime.EFFECT_QUALITY *
        runtime.energyEffectScale(),
    );
    const total = Math.min(requested, runtime.particleCapacity() - runtime.game.particles.length);
    for (let i = 0; i < total; i += 1) {
      const bag = i % 7 === 0;
      runtime.game.particles.push({
        type: bag ? 'moneyBag' : 'coin',
        x: runtime.visualRand(24, runtime.W - 24),
        y: runtime.visualRand(-180, -12),
        vx: runtime.visualRand(-36, 36),
        vy: runtime.visualRand(170, 360),
        r: bag ? runtime.visualRand(9, 14) : runtime.visualRand(5, 8),
        color: bag ? '#74ff8d' : '#ffd166',
        life: runtime.visualRand(1.45, 2.35),
        maxLife: 2.35,
        spin: runtime.visualRand(0, Math.PI * 2),
        spinSpeed: runtime.visualRand(-7, 7),
        label: bag ? '$' : i % 5 === 0 ? `$${runtime.formatBigNumber(reward)}` : '$',
      });
    }
  };
  runtime.explode = function (x, y, color, count, scale = 1) {
    runtime.spark(x, y, color, count);
    const qualityScale =
      runtime.game.drawQuality === 'low' ? 0.42 : runtime.game.drawQuality === 'medium' ? 0.72 : 1;
    const total = Math.min(
      Math.floor(
        count * 0.45 * qualityScale * runtime.EFFECT_QUALITY * runtime.energyEffectScale(),
      ),
      runtime.particleCapacity() - runtime.game.particles.length,
    );
    for (let i = 0; i < total; i += 1) {
      const angle = runtime.visualRand(0, Math.PI * 2);
      const speed = runtime.visualRand(160, 430) * scale;
      const life = runtime.visualRand(0.35, 0.82);
      runtime.game.particles.push({
        x,
        y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        r: runtime.visualRand(3, 8) * scale,
        color,
        life,
        maxLife: life,
      });
    }
  };
  runtime.shockwave = function (x, y, color, scale = 1) {
    const waveBudget =
      runtime.game.drawQuality === 'low' ? 30 : runtime.game.drawQuality === 'medium' ? 48 : 72;
    const total = Math.min(
      Math.ceil(waveBudget * runtime.EFFECT_QUALITY * runtime.energyEffectScale()),
      runtime.particleCapacity() - runtime.game.particles.length,
    );
    for (let i = 0; i < total; i += 1) {
      const angle = (i / Math.max(1, total)) * Math.PI * 2;
      const speed = runtime.visualRand(260, 620) * scale;
      const life = runtime.visualRand(0.52, 1.05) * runtime.clamp(scale, 1, 2.4);
      runtime.game.particles.push({
        x,
        y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        r: runtime.visualRand(2.5, 6.5) * runtime.clamp(scale, 1, 3),
        color,
        life,
        maxLife: life,
      });
    }
  };
  runtime.brickColor = function (brick) {
    const ratio = brick.hp / Math.max(1, brick.maxHp);
    if (brick.maxHp > runtime.game.level * 1.45) return ratio > 0.5 ? '#e05b24' : '#ffd166';
    if (brick.hp > runtime.game.level * 0.9) return '#c77a25';
    return ratio > 0.42 ? '#2fe6d0' : '#f5b041';
  };
  runtime.brightenColor = function (color) {
    if (color === '#e05b24') return '#ff8b3d';
    if (color === '#c77a25') return '#ffc15a';
    if (color === '#f5b041') return '#ffe08a';
    if (color === '#ffd166') return '#ffe08f';
    if (color === '#8b5cf6') return '#d69a52';
    if (color === '#2fe6d0') return '#fff1a8';
    if (color === '#74ff8d') return '#a4ffb4';
    return color;
  };
  runtime.orbColor = function (type) {
    if (type === 'ball') return '#b9ffb0';
    if (type === 'split') return '#ffd166';
    if (type === 'coin') return '#ffd166';
    if (type === 'clear5' || type === 'clear10') return '#ffd166';
    if (type === 'halve') return '#ff8a3d';
    if (type === 'chaos') return '#fff8dc';
    return '#ff8a3d';
  };
  runtime.orbLabel = function (type) {
    if (type === 'ball') return '+1';
    if (type === 'split') return 'x';
    if (type === 'coin') return '$';
    if (type === 'clear5') return '5';
    if (type === 'clear10') return '10';
    if (type === 'halve') return '½';
    if (type === 'chaos') return '50';
    return '!';
  };
}
